/**
 * OWA-842 (and OWA-1018): saves the server refused for good, which the queue dropped. The store
 * keeps them in `misc.refusedSaves` so a notice can tell the user why their change did not stick
 * (the notice itself is built under OWA-525). One entry per record: a later refusal replaces it.
 */

export const SAVE_REFUSED = 'SAVE_REFUSED';
export const REFUSED_SAVE_DISMISSED = 'REFUSED_SAVE_DISMISSED';

export type RefusedSave = {
    /** The entity name, for example 'task' */
    entity: string,
    id: string,
    /** The server's refusal code, for example 'ASSIGNEE_LOCKED' */
    code: string,
    /** The server's message, shown to the user */
    message: string,
    /** When this tab learned of it (milliseconds) */
    at: number,
};

export type RefusedSaveAction =
    | {type: typeof SAVE_REFUSED, refusal: RefusedSave}
    | {type: typeof REFUSED_SAVE_DISMISSED, entity: string, id: string};

/** A save of `entity` `id` was refused for good with `code`. */
export const saveRefused = (refusal: RefusedSave): RefusedSaveAction => ({type: SAVE_REFUSED, refusal});

/** The user has seen the refusal of `entity` `id`. */
export const dismissRefusedSave = (entity: string, id: string): RefusedSaveAction => ({type: REFUSED_SAVE_DISMISSED, entity, id});

const sameRecord = (a: {entity: string, id: string}, b: {entity: string, id: string}) => a.entity === b.entity && a.id === b.id;

/** The list of refused saves (misc.refusedSaves). */
export function refusedSaves(state: RefusedSave[] = [], action: {type?: unknown} | RefusedSaveAction): RefusedSave[] {
    switch (action.type) {
        case SAVE_REFUSED: {
            const {refusal} = action as Extract<RefusedSaveAction, {type: typeof SAVE_REFUSED}>;
            return [...state.filter(entry => !sameRecord(entry, refusal)), refusal];
        }
        case REFUSED_SAVE_DISMISSED: {
            const dismissed = action as Extract<RefusedSaveAction, {type: typeof REFUSED_SAVE_DISMISSED}>;
            return state.some(entry => sameRecord(entry, dismissed)) ? state.filter(entry => !sameRecord(entry, dismissed)) : state;
        }
        default:
            return state;
    }
}
